import { useBaseModalToast } from '@trustoo/components/modal/hooks/useBaseModalToast'
import { useToastActions } from '@trustoo/components/ui/toaster/ToastContext'
import Toaster from '@trustoo/components/ui/toaster/Toaster'
import type { ToastActions } from '@trustoo/components/ui/toaster/types/toastTypes'
import {
    AlertCircle,
    AlertTriangle,
    CheckCircle,
    CurrencyDollar,
    InfoCircle,
    RefreshCcw01,
    Send01,
    SlashCircle01,
    UserMinus01,
} from '@untitledui/icons'
import { setToastBridge } from 'components/misc/toastBridge'
import { isValidElement, useEffect, useMemo } from 'react'
import type { ReactElement } from 'react'
import type { FileRejection } from 'react-dropzone'

type ToastIcon = ReactElement | string | false

export interface ToastOptions {
    id?: string | null
    msg?: string
    icon?: ToastIcon
    red?: boolean
    green?: boolean
    orange?: boolean
    fromTop?: boolean
    fixed?: boolean
    timeout?: number | null
    closeBtn?: boolean
}

export interface OfficeToastApi {
    addToast: (options?: ToastOptions) => void
    deleteToast: (id: string) => void
    deleteAllToasts: () => void
    onFilesRejected: (files: FileRejection[]) => void
}

/**
 * Maps the legacy Material Symbol icon names used by `addToast` callers to
 * `@untitledui/icons` components, so existing `addToast({ icon: 'check_circle' })`
 * call sites keep working without touching every consumer.
 */
const ICON_BY_NAME: Record<string, ReactElement> = {
    check_circle: <CheckCircle />,
    error: <AlertCircle />,
    warning: <AlertTriangle />,
    accessible_forward: <CheckCircle />,
    no_accounts: <SlashCircle01 />,
    money_off: <SlashCircle01 />,
    person_remove: <UserMinus01 />,
    attach_money: <CurrencyDollar />,
    rowing: <Send01 />,
    lock_reset: <RefreshCcw01 />,
}

const resolveIcon = (icon: ToastIcon | undefined): ReactElement | false => {
    if (icon === false) {
        return false
    }

    if (isValidElement(icon)) {
        return icon
    }

    if (typeof icon === 'string') {
        return ICON_BY_NAME[icon] ?? <InfoCircle />
    }

    return <InfoCircle />
}

/**
 * Builds office's toast API on top of the shared, redux-independent toaster actions.
 * Resolves office's string icon names to elements before delegating, so the shared
 * store only ever holds `ReactElement | false`.
 */
export const createOfficeToastApi = (actions: ToastActions): OfficeToastApi => {
    const addToast = (options: ToastOptions = {}) => {
        const { icon, ...rest } = options
        actions.addToast({ ...rest, icon: resolveIcon(icon) })
    }

    const onFilesRejected = (files: FileRejection[]) =>
        files.forEach(({ file, errors }) =>
            errors.forEach((e) =>
                addToast({
                    msg: `${file.name} (${Math.round(file.size / 100000) / 10}MB) - ${e.code
                        .replace(/-/g, ' ')
                        .toUpperCase()} - ${e.message}`,
                }),
            ),
        )

    return {
        addToast,
        deleteToast: actions.deleteToast,
        deleteAllToasts: actions.deleteAllToasts,
        onFilesRejected,
    }
}

/**
 * Drop-in replacement for the old redux-backed hook, backed by the shared
 * `ToastProvider` context instead of the redux store. Consumes only the
 * (referentially stable) actions context — never `useToastState` — so firing a
 * toast doesn't subscribe the caller to toast state and re-render it on every
 * add/remove. Only the `Toaster` renderer reads state.
 */
export function useToast(): OfficeToastApi {
    const actions = useToastActions()

    // Memoised on the (stable) actions, so `addToast` keeps its identity across renders and a
    // caller can safely list it in an effect's dependencies.
    return useMemo(() => createOfficeToastApi(actions), [actions])
}

export interface OfficeModalToastApi extends Omit<OfficeToastApi, 'addToast'> {
    addModalToast: OfficeToastApi['addToast']
}

/** Toasts for the current NiceModal's BaseModal. Use useToast for messages that outlive the modal. */
export function useModalToast(): OfficeModalToastApi {
    const actions = useBaseModalToast()
    return useMemo(() => {
        const { addToast: addModalToast, ...rest } = createOfficeToastApi(actions)
        return { addModalToast, ...rest }
    }, [actions])
}

/**
 * Registers office's toast API into the module-level bridge so non-React code
 * (redux thunks, connected `mapDispatchToProps`) can fire toasts. Mount once,
 * inside `ToastProvider`.
 */
export const ToastBridgeConnector = () => {
    const actions = useToastActions()

    useEffect(() => {
        setToastBridge(createOfficeToastApi(actions))

        return () => setToastBridge(null)
    }, [actions])

    return null
}

export default Toaster
